import React from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../chrome/TopBar';
import ResumeInlineBanner from '../appNotice/ResumeInlineBanner';
import MobileTitleBar from './MobileTitleBar';
import { useUser } from '../../context/UserContext';
import { useAuth } from '../../context/AuthContext';
import useIsMobile from '../../hooks/useIsMobile';

/**
 * Page frame for the redesigned event + club surfaces.
 *
 * Desktop: TopBar (68px, sticky) → optional full-bleed `band` → a 1440px column with 32px
 * gutters, 24px top, 64px bottom, 16px between stacked sections.
 *
 * Mobile: the collapsing title bar (`mobileTitle`, `mobileRight`, `mobileEyebrow`) replaces
 * the TopBar — this is the handoff's mobile frame — and children render full-bleed; pages
 * pad their own content at 12px. Pass `showTopBarOnMobile` to keep the TopBar too.
 *
 * `embedded` (MobileEventSheet, previews) drops every piece of chrome.
 */
export default function SurfacePage({
  children,
  band = null,
  mobileTitle,
  mobileEyebrow,
  mobileRight,
  onBack,
  mobileDark = false,
  showTopBarOnMobile = false,
  embedded = false,
  contentClassName = '',
  className = '',
  maxWidth = 1440,
  /** `bare` — the page lays out its own column (the host event page: 1280px, 24/28/64). */
  bare = false,
  /** Force the mobile or desktop frame regardless of the viewport (the review step's previews). */
  mobile: mobileProp = null,
}) {
  const navigate = useNavigate();
  const viewportMobile = useIsMobile();
  const mobile = mobileProp === null || mobileProp === undefined ? viewportMobile : !!mobileProp;
  const { profile, walletBalance } = useUser();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (embedded) {
    return <div className={`bg-hand-canvas font-mono ${className}`}>{band}{children}</div>;
  }

  if (mobile) {
    return (
      <div className={`min-h-screen overflow-x-clip bg-hand-canvas font-mono ${className}`} data-surface="mobile">
        {showTopBarOnMobile ? (
          <TopBar user={profile || user} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/featured')} />
        ) : (
          <MobileTitleBar title={mobileTitle} eyebrow={mobileEyebrow} right={mobileRight} onBack={onBack} dark={mobileDark} />
        )}
        {band}
        <div className={contentClassName}>{children}</div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen overflow-x-clip bg-hand-canvas font-mono ${className}`} data-surface="desktop">
      <TopBar user={profile || user} walletBalance={walletBalance} onLogout={handleLogout} onLogoClick={() => navigate('/featured')} />
      <ResumeInlineBanner />
      {band}
      {bare ? children : (
        <div className={`mx-auto flex w-full flex-col gap-4 ${contentClassName}`} style={{ maxWidth, padding: '24px 32px 64px' }}>
          {children}
        </div>
      )}
    </div>
  );
}
